import { Box, Stack, styled, Typography } from "@mui/material"
import type React from "react"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import { SearchInput } from "../Shared/SearchInput"
import { useAlertCuration } from "./AlertCurationContext"
import { useAlertCandidatesLastUpdated } from "./useAlertCandidates"

const SectionTitle = styled(Typography)(() => ({
  color: extraColors.white,
  letterSpacing: "-0.01em",
}))

const SubSectionTitle = styled(Typography)(() => ({
  color: extraColors.translucent50,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  fontSize: "0.75rem",
}))

const ToggleContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "isArchived",
})<{ isArchived: boolean }>(({ isArchived }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "160px",
  height: "32px",
  backgroundColor: extraColors.translucent5,
  borderRadius: "8px",
  border: `1px solid ${extraColors.translucent8}`,
  cursor: "pointer",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    left: isArchived ? "80px" : "4px",
    width: "76px",
    height: "24px",
    backgroundColor: extraColors.status.navyLight,
    borderRadius: "6px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
}))

const ToggleLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== "active",
})<{ active: boolean }>(({ active }) => ({
  position: "relative",
  flex: 1,
  textAlign: "center",
  zIndex: 1,
  fontSize: "0.65rem",
  fontWeight: 900,
  letterSpacing: "0.05em",
  color: active ? extraColors.dark : extraColors.hint,
  transition: "color 0.3s ease",
}))

const LastUpdatedBlock = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
}))

const LastUpdatedLabel = styled(Typography)(() => ({
  color: extraColors.hint,
})) as typeof Typography

const LastUpdatedValue = styled(Typography)(() => ({
  color: extraColors.status.greenLight,
})) as typeof Typography

export const AlertCandidatesHeader: React.FC = () => {
  const {
    alertCurationFilters,
    setAlertCurationFilters,
    isArchivedView,
    toggleArchivedView,
  } = useAlertCuration()
  const { search } = alertCurationFilters

  const { data: lastUpdated } = useAlertCandidatesLastUpdated()
  const lastUpdatedAgo = dayjsUTC().to(dayjsUTC(lastUpdated))

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="column">
          <SectionTitle variant="h3Bold">Signals</SectionTitle>
          <SubSectionTitle variant="body1Bold">
            Discovery & Clustering
          </SubSectionTitle>
        </Stack>
        <LastUpdatedBlock>
          <LastUpdatedLabel variant="tiny">Last updated:</LastUpdatedLabel>
          <LastUpdatedValue variant="small1">{lastUpdatedAgo}</LastUpdatedValue>
        </LastUpdatedBlock>
        <SearchInput
          onChange={e =>
            setAlertCurationFilters({
              ...alertCurationFilters,
              search: e.target.value,
            })
          }
          searchText={search}
          placeholder="Search by keyword"
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "center",
        }}
      >
        <ToggleContainer
          isArchived={isArchivedView}
          onClick={toggleArchivedView}
        >
          <ToggleLabel active={!isArchivedView}>CURRENT</ToggleLabel>
          <ToggleLabel active={isArchivedView}>ARCHIVED</ToggleLabel>
        </ToggleContainer>
      </Stack>
    </Stack>
  )
}
